import { TokenClaims } from '@azure/msal-common';
import { useVariable } from '@softwareimaging/backstage';
import { useUserback } from '@userback/react';
import { useMemo } from 'react';
import { Variables } from '../../backstage/config';
import { useAuthentication } from '../useAuthProvider';

type ExtensionKey<T extends string> = `extension_${string}_${T}`;
type Extension<T extends string> = Record<ExtensionKey<T>, string>;

export enum RoleType {
  Admin = 'ADMIN',
  Partner = 'PARTNER',
  Company = 'COMPANY',
  CreditChecker = 'CREDITCHECKER'
}

export interface Role {
  type: RoleType;
  customerId?: string;
  partnerId?: string;
  creditCheckerId?: string;
}

interface User {
  firstName?: string;
  lastName?: string;
  email: string;
  roles: Role[];
  role: Role;
  id: string;
  claims?: TokenClaims;
}

export const useUser = (): User => {
  const environment = useVariable<Variables>('claimEnvironment');
  const account = useAuthentication(state => state.account);
  if (!account || !environment) throw new Error('Missing accounts');

  const { setEmail, setName } = useUserback();

  return useMemo(() => {
    const firstName = account.idTokenClaims?.given_name as string | undefined;
    const lastName = account.idTokenClaims?.family_name as string | undefined;

    const onlyEmail = account.idTokenClaims?.email as string;
    const allEmails = account.idTokenClaims?.emails as string[];
    const email = onlyEmail ?? allEmails[0];
    const id = account.idTokenClaims?.sub as string;

    const claims = account.idTokenClaims as TokenClaims & Extension<'roles'>;

    const raw = claims[`extension_${environment}_roles`];
    if (!raw) throw new Error('Missing roles');

    const roles: Role[] = raw.split(',').map(role => {
      const [type, id] = role.split(':') as [RoleType, string | undefined];
      if (type === RoleType.Admin) return { type: type as RoleType };
      if (type === RoleType.CreditChecker) return { type: type as RoleType, creditCheckerId: id };
      if (!id) throw new Error('Missing id');

      if (type === RoleType.Partner) return { type: type as RoleType, partnerId: id };
      if (type === RoleType.Company) {
        const [partnerId, customerId] = id.split('/');
        return { type: type as RoleType, customerId, partnerId };
      }
      throw new Error('Invalid role');
    });

    if (!email || !roles || roles.length === 0) throw new Error('Missing claims');

    setName(`${firstName} ${lastName}`);
    setEmail(email);

    return {
      firstName,
      lastName,
      email,
      claims,
      roles,
      id,
      role: roles[0]
    };
  }, [account, environment]);
};

export const useRole = (): Role => {
  const user = useUser();
  return user.role;
};

export const usePartnerId = (): string => {
  const role = useRole();
  if (!role.partnerId) return '';
  return role.partnerId;
};

export const useCompanyId = (): string | undefined => {
  const role = useRole();
  if (!role.customerId) return undefined;
  return role.customerId;
};

export const useCreditCheckerId = (): string => {
  const role = useRole();
  if (!role.creditCheckerId) return '';
  return role.creditCheckerId;
};
